import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { useAuthContext } from '~/shared/context/useAuthContext';
import { getVolumeDataQueryScopeParts } from '~/shared/utils/organizationUtils';
import { useXFetch } from '~/shared/utils/useXFetch';

import {
  explorationReducer,
  ExplorationDataContext,
  ExplorationDispatchContext,
  ExplorationStateContext,
  initialExplorationState,
  type ExplorationDataContextValue,
} from './ExplorationContext';
import { isExplorationOrgAggregateAccount } from '../lib/explorationAccountUtils';
import { buildAccountOptions } from '../lib/buildAccountOptions';
import {
  resolveExplorationUrlSnapshot,
  EXPLORATION_BY_ACCOUNT_PARAM,
} from '../lib/explorationUrlParams';
import { fetchExplorationVolumeCaches } from '../lib/fetchExplorationVolumeCaches';
import type {
  ExplorationVolumeLoadResult,
  LoadingProgress,
} from '../types/exploration';

export function ExplorationRootProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(explorationReducer, initialExplorationState);
  const [searchParams] = useSearchParams();
  const api = useXFetch();
  const authContext = useAuthContext();
  const authAccounts = authContext.accounts;
  const activeTenantId = authContext.activeTenantId;
  const authReady = !authContext.loading && !!authAccounts?.length;

  const volumeScopeKey = useMemo(
    () => getVolumeDataQueryScopeParts(authAccounts, activeTenantId),
    [authAccounts, activeTenantId]
  );
  const volumesQueryKey = useMemo(
    () => ['exploration', 'volumes', ...volumeScopeKey] as const,
    [volumeScopeKey]
  );
  const queryClient = useQueryClient();
  const [loadingProgress, setLoadingProgress] = useState<LoadingProgress | null>(null);
  const orgByAccountDefaultAppliedRef = useRef(false);

  const volumesQuery = useQuery<ExplorationVolumeLoadResult>({
    queryKey: volumesQueryKey,
    enabled: authReady,
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      if (!authAccounts?.length) return { accounts: {}, apiVolumeByEbsId: {} };

      const acct = authAccounts.find((a) => a.accountId === activeTenantId) ?? authAccounts[0];
      const name = acct?.accountName || acct?.accountId || '';
      setLoadingProgress({ loaded: 0, total: 0, status: 'Connecting…', accountName: name });
      try {
        return await fetchExplorationVolumeCaches(
          api,
          authAccounts,
          activeTenantId,
          setLoadingProgress,
          signal
        );
      } finally {
        setLoadingProgress(null);
      }
    },
  });

  const accounts = volumesQuery.data?.accounts ?? {};
  const apiVolumeByEbsId = volumesQuery.data?.apiVolumeByEbsId ?? {};

  const accountOptions = useMemo(() => buildAccountOptions(accounts), [accounts]);

  useEffect(() => {
    if (!accountOptions.length) return;

    const urlAccountId = resolveExplorationUrlSnapshot(searchParams, accounts).accountId;
    if (urlAccountId) return;

    if (!state.accountId || !accounts[state.accountId]) {
      dispatch({ type: 'setAccount', accountId: accountOptions[0].id });
    }
  }, [accountOptions, accounts, searchParams, state.accountId]);

  useEffect(() => {
    if (state.groupByAccount && !isExplorationOrgAggregateAccount(state.accountId)) {
      dispatch({ type: 'setGroupByAccount', groupByAccount: false });
    }
  }, [state.accountId, state.groupByAccount]);

  useEffect(() => {
    if (orgByAccountDefaultAppliedRef.current) return;
    if (!state.accountId) return;
    if (!isExplorationOrgAggregateAccount(state.accountId)) {
      orgByAccountDefaultAppliedRef.current = true;
      return;
    }
    if (searchParams.has(EXPLORATION_BY_ACCOUNT_PARAM)) {
      orgByAccountDefaultAppliedRef.current = true;
      return;
    }
    orgByAccountDefaultAppliedRef.current = true;
    if (!state.groupByAccount) {
      dispatch({ type: 'setGroupByAccount', groupByAccount: true, preserveDrillPath: true });
    }
  }, [searchParams, state.accountId, state.groupByAccount]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: volumesQueryKey });
  }, [queryClient, volumesQueryKey]);

  const loading = authContext.loading || volumesQuery.isLoading || volumesQuery.isFetching;
  const initialLoading = authContext.loading || volumesQuery.isLoading;

  const dataValue: ExplorationDataContextValue = {
    accounts,
    apiVolumeByEbsId,
    loading,
    initialLoading,
    loadingProgress,
    error: volumesQuery.error?.message ?? null,
    refresh,
  };

  return (
    <ExplorationStateContext.Provider value={state}>
      <ExplorationDispatchContext.Provider value={dispatch}>
        <ExplorationDataContext.Provider value={dataValue}>
          {children}
        </ExplorationDataContext.Provider>
      </ExplorationDispatchContext.Provider>
    </ExplorationStateContext.Provider>
  );
}
